/**
 * Low-level HTTP client for the Adieuu API.
 */
import { API_ERROR_SESSION_EXPIRED } from '../constants/api-errors';
import { CSRF_HEADER_NAME, readCsrfCookie } from './csrf';
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const API_ERROR_CAPTCHA_REQUIRED = 'CAPTCHA_REQUIRED';
let captchaHandler = null;
/**
 * Registers a global handler that is invoked when any API request receives
 * a CAPTCHA_REQUIRED error. The handler should show a captcha dialog and
 * resolve with the FriendlyCaptcha response token, or `null` if cancelled.
 * The client will automatically retry the failed request with the token.
 */
export function registerCaptchaHandler(handler) {
    captchaHandler = handler;
}
export function clearCaptchaHandler() {
    captchaHandler = null;
}
let unauthorizedProbe = null;
/**
 * Registers a probe invoked when any API request receives HTTP 401,
 * except GET /api/auth/session (that call is the probe itself).
 * The probe should refetch the session and sign the user out only if it is gone.
 */
export function registerUnauthorizedProbe(handler) {
    unauthorizedProbe = handler;
    return () => {
        if (unauthorizedProbe === handler)
            unauthorizedProbe = null;
    };
}
export function clearUnauthorizedProbe() {
    unauthorizedProbe = null;
}
function isSessionProbePath(path) {
    return path === '/api/auth/session' || path.endsWith('/api/auth/session');
}
export class ApiClient {
    baseUrl;
    defaultHeaders;
    timeout;
    fetchImpl;
    onSessionExpired;
    constructor(config) {
        this.baseUrl = config.baseUrl.replace(/\/$/, '');
        this.defaultHeaders = {
            'Content-Type': 'application/json',
            ...config.headers,
        };
        this.timeout = config.timeout ?? 30000;
        this.fetchImpl = config.fetchImpl ?? globalThis.fetch.bind(globalThis);
        this.onSessionExpired = config.onSessionExpired;
    }
    async request(method, path, body, options, isRetry = false, networkRetry = false) {
        const url = `${this.baseUrl}${path}`;
        const headers = {
            ...this.defaultHeaders,
            ...options?.headers,
        };
        if (MUTATING_METHODS.has(method.toUpperCase())) {
            const csrfToken = readCsrfCookie();
            if (csrfToken) {
                headers[CSRF_HEADER_NAME] = csrfToken;
            }
        }
        const timeoutController = new AbortController();
        const timeoutId = setTimeout(() => timeoutController.abort(), this.timeout);
        let fetchSignal;
        if (options?.signal) {
            const anyFn = AbortSignal.any;
            fetchSignal =
                typeof anyFn === 'function'
                    ? anyFn([options.signal, timeoutController.signal])
                    : options.signal;
        }
        else {
            fetchSignal = timeoutController.signal;
        }
        try {
            const response = await this.fetchImpl(url, {
                method,
                headers,
                body: body ? JSON.stringify(body) : undefined,
                signal: fetchSignal,
                credentials: 'include',
                ...(options?.cache ? { cache: options.cache } : {}),
            });
            clearTimeout(timeoutId);
            const data = (await response.json());
            if (response.status === 401 && !isSessionProbePath(path) && unauthorizedProbe) {
                unauthorizedProbe();
            }
            if (!data.success &&
                data.error?.code === API_ERROR_SESSION_EXPIRED &&
                this.onSessionExpired) {
                this.onSessionExpired();
            }
            if (!data.success &&
                data.error?.code === API_ERROR_CAPTCHA_REQUIRED &&
                captchaHandler &&
                !isRetry) {
                const token = await captchaHandler();
                if (token) {
                    const NO_BODY_METHODS = new Set(['GET', 'HEAD']);
                    if (NO_BODY_METHODS.has(method.toUpperCase())) {
                        const retryOptions = {
                            ...options,
                            headers: { ...options?.headers, 'X-FRC-Captcha-Response': token },
                        };
                        return this.request(method, path, undefined, retryOptions, true, networkRetry);
                    }
                    const retryBody = {
                        ...(body ?? {}),
                        'frc-captcha-response': token,
                    };
                    return this.request(method, path, retryBody, options, true, networkRetry);
                }
            }
            return data;
        }
        catch (error) {
            clearTimeout(timeoutId);
            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    if (options?.signal?.aborted) {
                        throw new DOMException('Aborted', 'AbortError');
                    }
                    return {
                        success: false,
                        error: {
                            code: 'TIMEOUT',
                            message: 'Request timed out',
                        },
                    };
                }
                if (!networkRetry &&
                    !MUTATING_METHODS.has(method.toUpperCase()) &&
                    !options?.signal?.aborted) {
                    await new Promise((r) => setTimeout(r, 500));
                    return this.request(method, path, body, options, isRetry, true);
                }
                return {
                    success: false,
                    error: {
                        code: 'NETWORK_ERROR',
                        message: error.message || 'Network error',
                    },
                };
            }
            if (!networkRetry &&
                !MUTATING_METHODS.has(method.toUpperCase()) &&
                !options?.signal?.aborted) {
                await new Promise((r) => setTimeout(r, 500));
                return this.request(method, path, body, options, isRetry, true);
            }
            return {
                success: false,
                error: {
                    code: 'UNKNOWN_ERROR',
                    message: 'An unknown error occurred',
                },
            };
        }
    }
    async get(path, options) {
        return this.request('GET', path, undefined, options);
    }
    async post(path, body, options) {
        return this.request('POST', path, body, options);
    }
    async put(path, body, options) {
        return this.request('PUT', path, body, options);
    }
    async patch(path, body, options) {
        return this.request('PATCH', path, body, options);
    }
    async delete(path, options) {
        return this.request('DELETE', path, options?.body, options);
    }
}
//# sourceMappingURL=http-client.js.map